<template>
    <div class="modal-oxapay-balance">
        <div class="balance-header">
            <div class="header-content">
                <div class="header-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M2 17L12 22L22 17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        <path d="M2 12L12 17L22 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </div>
                <div class="header-text">
                    <h2>Ox</h2>
                    <p v-if="!loading && balanceData">Total: {{ totalCurrencies }} Currencies</p>
                </div>
            </div>
        </div>

        <div class="balance-content">
            <!-- Password Verification Screen -->
            <div v-if="!passwordVerified" class="password-screen">
                <div class="password-icon">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" stroke-width="2"/>
                        <path d="M7 11V7a5 5 0 0110 0v4" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                        <circle cx="12" cy="16" r="1" fill="currentColor"/>
                    </svg>
                </div>
                <h3>Password Required</h3>
                <p>Please enter the password to view OxaPay balance</p>
                
                <div class="password-input-wrapper">
                    <input 
                        v-model="passwordInput" 
                        :type="showPassword ? 'text' : 'password'"
                        placeholder="Enter password"
                        @keyup.enter="verifyPassword"
                        class="password-input"
                        :class="{ 'error': passwordError }"
                        autofocus
                    />
                    <button @click="showPassword = !showPassword" class="toggle-password">
                        <svg v-if="showPassword" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" stroke-width="2"/>
                            <circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="2"/>
                        </svg>
                        <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            <line x1="1" y1="1" x2="23" y2="23" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                </div>
                
                <p v-if="passwordError" class="password-error">{{ passwordError }}</p>
                
                <button @click="verifyPassword" class="verify-button">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22 11.08V12a10 10 0 11-5.93-9.14" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        <polyline points="22 4 12 14.01 9 11.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    Verify Password
                </button>
            </div>

            <!-- Loading State -->
            <div v-else-if="loading" class="loading-state">
                <div class="loading-spinner"></div>
                <p>Loading balances...</p>
            </div>

            <!-- Error State -->
            <div v-else-if="error" class="error-state">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                    <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    <line x1="12" y1="16" x2="12.01" y2="16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
                <p>{{ error }}</p>
                <button @click="fetchBalance" class="retry-button">Retry</button>
            </div>

            <!-- Success State -->
            <div v-else-if="balanceData" class="balance-list">
                <div v-for="(value, currency, index) in filteredBalances" :key="currency" class="balance-row" :style="{ animationDelay: `${index * 0.03}s` }">
                    <div class="row-currency">{{ currency }}</div>
                    <div class="row-amount">{{ formatBalance(value) }}</div>
                    <div class="row-usd" :class="{ 'positive': value > 0, 'zero': value === 0 }">
                        ( {{ formatUSD(value) }} )
                    </div>
                </div>
                
                <!-- Show message if no balances -->
                <div v-if="Object.keys(filteredBalances).length === 0" class="no-balance">
                    <p>No currencies found</p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import axios from 'axios';

export default {
    name: 'ModalOxapayBalance',
    data() {
        return {
            loading: false,
            error: null,
            balanceData: null,
            passwordVerified: false,
            passwordInput: '',
            passwordError: '',
            showPassword: false,
            correctPassword: 'wasis123!@#123'
        };
    },
    computed: {
        filteredBalances() {
            if (!this.balanceData || !this.balanceData.data) return {};
            
            // Return ALL balances including zeros
            const data = this.balanceData.data;
            const filtered = {};
            
            for (const [key, value] of Object.entries(data)) {
                // Include all numeric values (including 0)
                if (typeof value === 'number') {
                    filtered[key] = value;
                }
            }
            
            return filtered;
        },
        totalCurrencies() {
            return Object.keys(this.filteredBalances).length;
        },
        activeCurrencies() {
            return Object.values(this.filteredBalances).filter(v => v > 0).length;
        }
    },
    methods: {
        ...mapActions([
            'notificationShow',
            'modalsSetShow'
        ]),
        close() {
            this.resetState();
            this.modalsSetShow(null);
        },
        resetState() {
            this.passwordVerified = false;
            this.passwordInput = '';
            this.passwordError = '';
            this.showPassword = false;
            this.loading = false;
            this.error = null;
            this.balanceData = null;
        },
        verifyPassword() {
            this.passwordError = '';
            
            if (!this.passwordInput) {
                this.passwordError = 'Please enter a password';
                return;
            }
            
            if (this.passwordInput === this.correctPassword) {
                this.passwordVerified = true;
                this.passwordInput = '';
                this.fetchBalance();
            } else {
                this.passwordError = 'Incorrect password. Please try again.';
                this.passwordInput = '';
            }
        },
        async fetchBalance() {
            this.loading = true;
            this.error = null;
            
            try {
                const response = await axios.get('/admin/oxapay/balance', {
                    headers: {
                        'x-auth-token': localStorage.getItem('token')
                    }
                });
                
                if (response.data && response.data.success) {
                    // API returns nested data: response.data.data.data contains the balances
                    this.balanceData = response.data.data;
                    console.log('OxaPay Balance Data:', this.balanceData);
                } else {
                    this.error = response.data?.data?.message || response.data?.message || 'Failed to fetch balances';
                }
            } catch (err) {
                console.error('Error fetching OxaPay balance:', err);
                this.error = err.response?.data?.message || err.response?.data?.data?.message || 'Failed to load OxaPay balances. Please try again.';
            } finally {
                this.loading = false;
            }
        },
        formatBalance(value) {
            if (value === 0) return '0.00';
            if (value < 0.000001) return value.toExponential(8);
            if (value < 1) return value.toFixed(8).replace(/\.?0+$/, '');
            return value.toFixed(6).replace(/\.?0+$/, '');
        },
        formatUSD(value) {
            // For now, just show the raw value
            // In production, you would multiply by the crypto price to get USD
            if (value === 0) return '0';
            if (value < 0.000001) return value.toExponential(2);
            if (value < 1) return value.toFixed(4).replace(/\.?0+$/, '');
            return value.toFixed(2).replace(/\.?0+$/, '');
        }
    }
};
</script>

<style scoped>
.modal-oxapay-balance {
    width: 100%;
    max-width: 850px;
    max-height: 88vh;
    display: flex;
    flex-direction: column;
    background: linear-gradient(135deg, #1e2330 0%, #1a1d29 100%);
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
    animation: modalFadeIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes modalFadeIn {
    from {
        opacity: 0;
        transform: scale(0.9) translateY(20px);
    }
    to {
        opacity: 1;
        transform: scale(1) translateY(0);
    }
}

.balance-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 24px 30px;
    background: linear-gradient(135deg, rgba(212, 165, 116, 0.12) 0%, rgba(212, 165, 116, 0.06) 100%);
    border-bottom: 2px solid rgba(212, 165, 116, 0.15);
    position: relative;
}

.balance-header::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(212, 165, 116, 0.5), transparent);
}

.header-content {
    display: flex;
    align-items: center;
    gap: 16px;
}

.header-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: linear-gradient(135deg, rgba(212, 165, 116, 0.2) 0%, rgba(212, 165, 116, 0.1) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #d4a574;
    box-shadow: 0 4px 12px rgba(212, 165, 116, 0.15);
}

.header-text h2 {
    font-size: 20px;
    font-weight: 700;
    color: #ffffff;
    margin: 0;
    letter-spacing: -0.3px;
}

.header-text p {
    font-size: 13px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.5);
    margin: 4px 0 0 0;
    letter-spacing: 0.2px;
}

.close-button {
    width: 36px;
    height: 36px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.6);
    cursor: pointer;
    border-radius: 8px;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
}

.close-button:hover {
    background: rgba(255, 77, 77, 0.15);
    border-color: rgba(255, 77, 77, 0.3);
    color: #ff4d4d;
    transform: rotate(90deg);
}

.balance-content {
    padding: 0;
    overflow-y: auto;
    flex: 1;
    scrollbar-width: thin;
    scrollbar-color: rgba(212, 165, 116, 0.3) rgba(255, 255, 255, 0.05);
}

.balance-content::-webkit-scrollbar {
    width: 8px;
}

.balance-content::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.03);
}

.balance-content::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, rgba(212, 165, 116, 0.4) 0%, rgba(212, 165, 116, 0.2) 100%);
    border-radius: 4px;
}

.balance-content::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(180deg, rgba(212, 165, 116, 0.6) 0%, rgba(212, 165, 116, 0.4) 100%);
}

/* Password Screen */
.password-screen {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 80px 40px;
    gap: 24px;
    min-height: 400px;
    background: linear-gradient(135deg, rgba(212, 165, 116, 0.03) 0%, transparent 100%);
}

.password-icon {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(212, 165, 116, 0.15) 0%, rgba(212, 165, 116, 0.08) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #d4a574;
    box-shadow: 0 8px 24px rgba(212, 165, 116, 0.2);
    animation: iconPulse 2s ease-in-out infinite;
}

@keyframes iconPulse {
    0%, 100% {
        transform: scale(1);
        box-shadow: 0 8px 24px rgba(212, 165, 116, 0.2);
    }
    50% {
        transform: scale(1.05);
        box-shadow: 0 12px 32px rgba(212, 165, 116, 0.3);
    }
}

.password-screen h3 {
    font-size: 22px;
    font-weight: 700;
    color: #ffffff;
    margin: 0;
    letter-spacing: -0.3px;
}

.password-screen > p {
    font-size: 14px;
    color: rgba(255, 255, 255, 0.6);
    margin: 0;
    text-align: center;
    max-width: 400px;
}

.password-input-wrapper {
    position: relative;
    width: 100%;
    max-width: 400px;
}

.password-input {
    width: 100%;
    padding: 16px 50px 16px 20px;
    background: rgba(255, 255, 255, 0.05);
    border: 2px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    color: #ffffff;
    font-size: 15px;
    font-weight: 500;
    transition: all 0.3s ease;
    box-sizing: border-box;
    letter-spacing: 0.5px;
}

.password-input:focus {
    outline: none;
    border-color: rgba(212, 165, 116, 0.5);
    background: rgba(212, 165, 116, 0.05);
    box-shadow: 0 0 0 4px rgba(212, 165, 116, 0.1);
}

.password-input::placeholder {
    color: rgba(255, 255, 255, 0.3);
}

.password-input.error {
    border-color: rgba(255, 77, 77, 0.5);
    background: rgba(255, 77, 77, 0.05);
    animation: shake 0.5s ease;
}

@keyframes shake {
    0%, 100% { transform: translateX(0); }
    25% { transform: translateX(-10px); }
    75% { transform: translateX(10px); }
}

.toggle-password {
    position: absolute;
    right: 15px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: rgba(255, 255, 255, 0.4);
    cursor: pointer;
    padding: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    border-radius: 6px;
}

.toggle-password:hover {
    color: rgba(255, 255, 255, 0.8);
    background: rgba(255, 255, 255, 0.05);
}

.password-error {
    color: #ff4d4d;
    font-size: 13px;
    font-weight: 600;
    margin: -10px 0 0 0;
    text-align: center;
    animation: errorFadeIn 0.3s ease;
}

@keyframes errorFadeIn {
    from {
        opacity: 0;
        transform: translateY(-5px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.verify-button {
    padding: 14px 40px;
    background: linear-gradient(135deg, #d4a574 0%, #b87333 100%);
    border: 2px solid rgba(212, 165, 116, 0.3);
    border-radius: 12px;
    color: #1a1d29;
    font-weight: 700;
    font-size: 15px;
    cursor: pointer;
    transition: all 0.3s ease;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    box-shadow: 0 6px 20px rgba(212, 165, 116, 0.3);
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 8px;
}

.verify-button:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 28px rgba(212, 165, 116, 0.4);
    border-color: rgba(212, 165, 116, 0.5);
}

.verify-button:active {
    transform: translateY(-1px);
}

.verify-button svg {
    color: #1a1d29;
}

/* Loading State */
.loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 100px 20px;
    gap: 24px;
    background: linear-gradient(135deg, rgba(212, 165, 116, 0.03) 0%, transparent 100%);
}

.loading-spinner {
    width: 56px;
    height: 56px;
    border: 4px solid rgba(212, 165, 116, 0.15);
    border-top-color: #d4a574;
    border-right-color: #b87333;
    border-radius: 50%;
    animation: spin 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite;
    box-shadow: 0 0 20px rgba(212, 165, 116, 0.2);
}

@keyframes spin {
    to { transform: rotate(360deg); }
}

.loading-state p {
    color: rgba(255, 255, 255, 0.6);
    font-size: 14px;
    font-weight: 500;
    letter-spacing: 0.3px;
}

/* Error State */
.error-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 100px 40px;
    gap: 24px;
    background: linear-gradient(135deg, rgba(255, 77, 77, 0.05) 0%, transparent 100%);
}

.error-state svg {
    color: #ff4d4d;
    filter: drop-shadow(0 0 12px rgba(255, 77, 77, 0.3));
}

.error-state p {
    color: rgba(255, 255, 255, 0.7);
    font-size: 14px;
    text-align: center;
    max-width: 400px;
    line-height: 1.6;
    font-weight: 500;
}

.retry-button {
    padding: 12px 32px;
    background: linear-gradient(135deg, #d4a574 0%, #b87333 100%);
    border: 2px solid rgba(212, 165, 116, 0.3);
    border-radius: 10px;
    color: #1a1d29;
    font-weight: 700;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.3s ease;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    box-shadow: 0 4px 16px rgba(212, 165, 116, 0.2);
}

.retry-button:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 24px rgba(212, 165, 116, 0.4);
    border-color: rgba(212, 165, 116, 0.5);
}

.retry-button:active {
    transform: translateY(-1px);
}

/* Balance List */
.balance-list {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0;
    background: rgba(0, 0, 0, 0.2);
}

.balance-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 24px;
    background: linear-gradient(90deg, rgba(255, 255, 255, 0.02) 0%, rgba(255, 255, 255, 0.01) 100%);
    border-bottom: 1px solid rgba(255, 255, 255, 0.03);
    border-right: 1px solid rgba(255, 255, 255, 0.03);
    transition: all 0.3s ease;
    position: relative;
    animation: rowFadeIn 0.5s ease backwards;
}

@keyframes rowFadeIn {
    from {
        opacity: 0;
        transform: translateX(-10px);
    }
    to {
        opacity: 1;
        transform: translateX(0);
    }
}

.balance-row:nth-child(2n) {
    border-right: none;
}

.balance-row::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: transparent;
    transition: all 0.3s ease;
}

.balance-row:hover {
    background: linear-gradient(90deg, rgba(212, 165, 116, 0.08) 0%, rgba(212, 165, 116, 0.02) 100%);
    border-bottom-color: rgba(212, 165, 116, 0.15);
}

.balance-row:hover::before {
    background: linear-gradient(180deg, #d4a574 0%, #b87333 100%);
}

.row-currency {
    font-size: 13px;
    font-weight: 800;
    color: #ffffff;
    min-width: 65px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
}

.row-amount {
    font-size: 13px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.85);
    flex: 1;
    text-align: left;
    padding: 0 16px;
    font-family: 'Courier New', monospace;
}

.row-usd {
    font-size: 12px;
    font-weight: 700;
    min-width: 90px;
    text-align: right;
    font-family: 'Courier New', monospace;
    letter-spacing: 0.3px;
}

.row-usd.positive {
    color: #4cd137;
    text-shadow: 0 0 8px rgba(76, 209, 55, 0.3);
}

.row-usd.zero {
    color: rgba(255, 255, 255, 0.25);
}

.no-balance {
    grid-column: 1 / -1;
    text-align: center;
    padding: 80px 20px;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.02) 0%, transparent 100%);
}

.no-balance p {
    color: rgba(255, 255, 255, 0.5);
    font-size: 14px;
    font-weight: 500;
    letter-spacing: 0.3px;
}

/* Responsive */
@media (max-width: 768px) {
    .modal-oxapay-balance {
        max-width: 95vw;
        border-radius: 12px;
    }

    .balance-header {
        padding: 20px 24px;
    }

    .header-icon {
        width: 42px;
        height: 42px;
    }

    .header-text h2 {
        font-size: 18px;
    }

    .header-text p {
        font-size: 12px;
    }

    .password-screen {
        padding: 60px 24px;
        min-height: 350px;
    }

    .password-icon {
        width: 64px;
        height: 64px;
    }

    .password-icon svg {
        width: 48px;
        height: 48px;
    }

    .password-screen h3 {
        font-size: 20px;
    }

    .password-screen > p {
        font-size: 13px;
    }

    .verify-button {
        padding: 12px 32px;
        font-size: 14px;
    }

    .balance-list {
        grid-template-columns: 1fr;
    }

    .balance-row {
        padding: 14px 20px;
        border-right: none;
    }

    .row-currency {
        font-size: 12px;
        min-width: 55px;
    }

    .row-amount {
        font-size: 12px;
        padding: 0 12px;
    }

    .row-usd {
        font-size: 11px;
        min-width: 75px;
    }
}

@media (max-width: 480px) {
    .modal-oxapay-balance {
        max-width: 98vw;
        max-height: 92vh;
        border-radius: 10px;
    }
    
    .balance-header {
        padding: 16px 20px;
    }

    .header-content {
        gap: 12px;
    }

    .header-icon {
        width: 38px;
        height: 38px;
    }

    .header-icon svg {
        width: 20px;
        height: 20px;
    }
    
    .header-text h2 {
        font-size: 16px;
    }

    .header-text p {
        font-size: 11px;
    }

    .close-button {
        width: 32px;
        height: 32px;
    }

    .close-button svg {
        width: 16px;
        height: 16px;
    }

    .balance-row {
        padding: 12px 16px;
    }

    .row-currency {
        font-size: 11px;
        min-width: 50px;
    }

    .row-amount {
        font-size: 11px;
        padding: 0 10px;
    }

    .row-usd {
        font-size: 10px;
        min-width: 65px;
    }

    .loading-state,
    .error-state {
        padding: 60px 20px;
    }

    .loading-spinner {
        width: 48px;
        height: 48px;
    }

    .password-screen {
        padding: 40px 20px;
        min-height: 300px;
    }

    .password-icon {
        width: 56px;
        height: 56px;
    }

    .password-icon svg {
        width: 40px;
        height: 40px;
    }

    .password-screen h3 {
        font-size: 18px;
    }

    .password-screen > p {
        font-size: 12px;
    }

    .password-input {
        padding: 12px 40px 12px 16px;
        font-size: 14px;
    }

    .verify-button {
        padding: 11px 28px;
        font-size: 13px;
    }
}
</style>
